import { useState, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Link as RouteLink } from 'react-router-dom';

import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import MenuItem from '@material-ui/core/MenuItem';
import Menu from '@material-ui/core/Menu';
import Divider from '@material-ui/core/Divider';
import Link from '@material-ui/core/Link';
import Typography from 'components/CncUI/Typography';
import Button from 'components/CncUI/Button';

import useAuthentication from 'hooks/useAuthentication';

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  root: {
    maxWidth: '100%',
    color: palette.cncUI.default['+10'],
    '&:hover, &:focus, &:active': {
      color: palette.cncUI.default['+10'],
    },
    '&:hover': {
      backgroundColor: palette.cncUI.default['+3'],
    },
    '& span.userName': {
      whiteSpace: 'nowrap',
      paddingLeft: 5,
      paddingRight: 5,
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      maxWidth: '70%',
      [breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    [breakpoints.down('sm')]: {
      paddingLeft: 0,
      paddingRight: 0,
      '& .MuiButton-startIcon, & .MuiButton-endIcon': {
        margin: 0,
      },
    },
  },
  menuUserAvatar: {
    fontSize: 56,
    gridArea: 'avatar',
    color: palette.primary.dark,
  },
  menuUserName: {
    gridArea: 'name',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: '85%',
  },
  menuUserEmail: {
    gridArea: 'email',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: '85%',
  },
  menuItemUser: {
    height: 130,
    pointerEvents: 'none',
    display: 'grid',
    placeItems: 'center',
    gridTemplateRows: '45% 1fr 1fr',
    paddingTop: '8%',
    paddingBottom: '8%',
    gridTemplateAreas: `
    "avatar"
    "name"
    "email"`,
  },
  menuItem: {
    placeItems: 'center',
    display: 'grid',
    padding: '11px 16px',
    '& .cncui-typography': {
      margin: '1px 0',
    },
  },
  menu: {
    width: 225,
    '& ul:first-child': {
      padding: 0,
    },
  },
  resetLink: {
    font: 'inherit',
    color: 'inherit',
    textDecoration: 'none',
  },
}));

const OKTA_SETTINGS_URL =
  process.env.REACT_APP_ENVIRONMENT === 'prod'
    ? 'https://login.castandcrew.com/enduser/settings'
    : 'https://login.dev.aws.castandcrew.com/enduser/settings';

const SUPPORT_URL = 'https://support.castandcrew.com/';

const CC_EDGE_URL = 'http://edge.castandcrew.com/learning/cc';

const UserMenu = () => {
  const classes = useStyles();
  const { user, logout } = useAuthentication();
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorEl = useRef(null);

  if (!user) {
    return null;
  }

  const fullName = [user.firstName, user.lastName].join(' ');

  return (
    <>
      <Button
        tertiary
        buttonRef={anchorEl}
        aria-owns={menuOpen ? 'menu-appbar' : null}
        aria-haspopup="true"
        onClick={() => setMenuOpen(!menuOpen)}
        className={classes.root}
        data-test-id={`UserMenu-button`}
        key={`UserMenu-button`}
        startIcon={<AccountCircleIcon />}
        endIcon={<KeyboardArrowDownIcon />}
      >
        <span className="userName" data-test-id="UserMenu-userName">
          {fullName}
        </span>
      </Button>
      <Menu
        disableAutoFocusItem
        anchorEl={() => anchorEl.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        classes={{ paper: classes.menu }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        getContentAnchorEl={null}
        data-test-id={`UserMenu-menu`}
        key={`UserMenu-menu`}
        BackdropProps={{
          'data-test-id': `UserMenu-backdrop`,
        }}
      >
        <div className={classes.menuItemUser}>
          <AccountCircleIcon
            className={classes.menuUserAvatar}
            data-test-id={`UserMenu-menuAvatar`}
          />
          <Typography
            className={classes.menuUserName}
            data-test-id={`UserMenu-menuUserName`}
          >
            {fullName}
          </Typography>
          <Typography
            variant="caption"
            className={classes.menuUserEmail}
            data-test-id={`UserMenu-menuEmail`}
          >
            {user.email}
          </Typography>
        </div>
        {!user.idp && [
          <Divider key="divider-nonidp-1" />,
          <Link
            color="inherit"
            variant="body2"
            href={OKTA_SETTINGS_URL}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            key="UserMenu-oktaSettings"
          >
            <MenuItem
              className={classes.menuItem}
              data-test-id={'UserMenu-oktaSettings'}
            >
              <Typography>Manage OKTA Account</Typography>
            </MenuItem>
          </Link>,
          <Divider key="divider-nonidp-2" />,
          <RouteLink
            to="/reset-password"
            className={classes.resetLink}
            key="UserMenu-resetPassword"
          >
            <MenuItem
              className={classes.menuItem}
              data-test-id={`UserMenu-resetPassword`}
            >
              <Typography>Reset Password</Typography>
            </MenuItem>
          </RouteLink>,
        ]}
        <Divider />
        <Link
          color="inherit"
          variant="body2"
          href={CC_EDGE_URL}
          target="_blank"
          rel="noopener noreferrer"
          underline="none"
        >
          <MenuItem
            className={classes.menuItem}
            data-test-id={`UserMenu-edgeTraining`}
          >
            <Typography>C&C Edge Training</Typography>
          </MenuItem>
        </Link>
        <Divider />
        <Link
          color="inherit"
          variant="body2"
          href={SUPPORT_URL}
          target="_blank"
          rel="noopener noreferrer"
          underline="none"
        >
          <MenuItem
            className={classes.menuItem}
            data-test-id={`UserMenu-helpCenter`}
          >
            <Typography>Help Center</Typography>
          </MenuItem>
        </Link>
        <Divider />
        <MenuItem
          onClick={() => logout()}
          className={classes.menuItem}
          data-test-id={`UserMenu-logout`}
        >
          <Typography>Sign Out</Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserMenu;
